<template>
  <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-lg">
      <div class="flex items-start justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold dark:text-gray-100">New user</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            <template v-if="accountType === 'internal'">
              Creates an internal staff user (no company). They'll get the role you pick below; you can adjust roles later on their profile.
            </template>
            <template v-else>
              Creates an external contact-role user tied to a company. They'll auto-promote to a real role on first OIDC sign-in.
            </template>
          </p>
        </div>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white text-2xl leading-none">&times;</button>
      </div>

      <div class="space-y-3">
        <!-- Account type -->
        <div>
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Account type</label>
          <div class="flex gap-2">
            <button v-for="opt in accountTypes" :key="opt.value"
              @click="accountType = opt.value"
              :class="['px-3 py-1.5 rounded-full text-xs', accountType === opt.value ? 'bg-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-600 dark:text-gray-100']">
              {{ opt.label }}
            </button>
          </div>
        </div>

        <!-- Name -->
        <div class="flex space-x-2">
          <div class="flex-1">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">First name</label>
            <input v-model="nameFirst" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" placeholder="First" />
          </div>
          <div class="flex-1">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Last name</label>
            <input v-model="nameLast" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" placeholder="Last" />
          </div>
        </div>

        <div>
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Email</label>
          <input v-model="email" type="email" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" placeholder="user@company.com" />
        </div>

        <div class="flex space-x-2">
          <div class="flex-1">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Phone</label>
            <input v-model="phone" type="tel" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" placeholder="(optional)" />
          </div>
          <div class="w-24">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Ext.</label>
            <input v-model="extension" type="text" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" />
          </div>
        </div>

        <!-- External: company (required) -->
        <div v-if="accountType === 'external'">
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Company</label>
          <input v-model="companySearch" @input="debouncedCompanySearch" class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm" placeholder="Search company…" />
          <div v-if="companyResults.length" class="mt-1 max-h-40 overflow-auto border border-gray-200 dark:border-gray-600 rounded-md">
            <div v-for="c in companyResults" :key="c.id"
              @click="selectCompany(c)"
              class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm dark:text-gray-100">
              {{ c.companyname }}
            </div>
          </div>
          <div v-if="selectedCompany" class="mt-1 text-xs text-gray-600 dark:text-gray-300">
            Selected: <span class="font-medium">{{ selectedCompany.companyname }}</span>
            <button @click="selectedCompany = null" class="ml-2 text-red-500 hover:underline">clear</button>
          </div>
        </div>

        <!-- Internal: starting role -->
        <div v-else>
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Starting role</label>
          <select v-model.number="roleId" :disabled="!assignableInternalRoles.length"
            class="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm disabled:opacity-50">
            <option v-for="r in assignableInternalRoles" :key="r.id" :value="r.id">{{ r.name }}</option>
          </select>
          <p v-if="!assignableInternalRoles.length" class="mt-1 text-xs text-red-500">
            Your role can't assign any internal role.
          </p>
          <p v-else class="mt-1 text-xs text-amber-600 dark:text-amber-300">
            You can assign roles at or below your own privilege level. Area 51 / CIA / FBI grant user-administration access — pick carefully.
          </p>
        </div>

        <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
      </div>

      <div class="flex justify-end gap-2 mt-5">
        <button @click="$emit('close')" class="px-4 py-2 rounded-md text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100">Cancel</button>
        <button @click="submit" :disabled="saving || !canSubmit"
          class="px-4 py-2 rounded-md text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-medium disabled:opacity-50">
          {{ saving ? 'Creating…' : 'Create user' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  props: { isVisible: { type: Boolean, default: false } },
  emits: ['close', 'created'],
  data() {
    return {
      accountType: 'external',
      accountTypes: [
        { value: 'external', label: 'External (contact)' },
        { value: 'internal', label: 'Internal (staff)' },
      ],
      nameFirst: '',
      nameLast: '',
      email: '',
      phone: '',
      extension: '',
      // external
      companySearch: '',
      companyResults: [],
      selectedCompany: null,
      companyTimer: null,
      // internal
      roleId: 30,
      internalRoles: [],
      currentUserRoles: [],
      saving: false,
      error: null,
    };
  },
  computed: {
    callerMinRole() {
      // Highest privilege = smallest role id the current admin holds.
      const ids = (this.currentUserRoles || []).map(Number).filter((n) => !isNaN(n));
      return ids.length ? Math.min(...ids) : 9999;
    },
    assignableInternalRoles() {
      // Can assign roles at or below your own privilege level (id >= your highest role).
      return this.internalRoles.filter((r) => Number(r.id) >= this.callerMinRole);
    },
    canSubmit() {
      const named = this.nameFirst.trim() && this.email.trim();
      if (!named) return false;
      return this.accountType === 'external' ? !!this.selectedCompany : !!this.roleId;
    },
  },
  watch: {
    isVisible(v) {
      if (v) {
        this.accountType = 'external';
        this.nameFirst = ''; this.nameLast = ''; this.email = ''; this.phone = ''; this.extension = '';
        this.companySearch = ''; this.companyResults = []; this.selectedCompany = null;
        this.roleId = 30;
        this.error = null;
        this.loadCurrentUserRoles();
        this.loadInternalRoles();
      }
    },
    assignableInternalRoles(list) {
      // Keep the selected role valid as the assignable set resolves. Prefer 30 (FBI).
      if (!list.some((r) => Number(r.id) === Number(this.roleId))) {
        this.roleId = list.some((r) => r.id === 30) ? 30 : (list[0]?.id ?? 0);
      }
    },
  },
  methods: {
    async loadCurrentUserRoles() {
      try {
        const res = await axios.get(`user/role/${localStorage.getItem('user_id')}`);
        this.currentUserRoles = Array.isArray(res.data?.roles) ? res.data.roles : [];
      } catch (e) {
        this.currentUserRoles = [];
      }
    },
    async loadInternalRoles() {
      if (this.internalRoles.length) return;
      try {
        const res = await axios.get('admin/roles');
        const all = Array.isArray(res.data) ? res.data : [];
        this.internalRoles = all.filter((r) => r.type === 'Internal');
      } catch (e) { /* ignore */ }
    },
    debouncedCompanySearch() {
      clearTimeout(this.companyTimer);
      this.companyTimer = setTimeout(async () => {
        try {
          const res = await axios.get('companies/search', { params: { q: this.companySearch } });
          this.companyResults = Array.isArray(res.data) ? res.data : [];
        } catch (e) { /* ignore */ }
      }, 250);
    },
    selectCompany(c) {
      this.selectedCompany = c;
      this.companyResults = [];
      this.companySearch = c.companyname;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.saving = true;
      this.error = null;
      this.$updateLoading(1);
      try {
        const payload = {
          requester:  `${this.nameFirst.trim()} ${this.nameLast.trim()}`.trim(),
          name_first: this.nameFirst.trim(),
          name_last:  this.nameLast.trim(),
          email:      this.email.trim(),
          phone:      this.phone.trim(),
          extension:  this.extension.trim(),
          account_type: this.accountType,
        };
        if (this.accountType === 'external') {
          payload.company_id = this.selectedCompany.id;
        } else {
          payload.role_id = Number(this.roleId || 0);
        }
        const res = await axios.post('requesters/add', payload);
        this.$emit('created', { id: res.data?.contact_id });
      } catch (e) {
        this.error = e?.response?.data?.message || 'Failed to create user.';
      } finally {
        this.saving = false;
        this.$updateLoading(-1);
      }
    },
  },
};
</script>
